/**
 * Path: packages/frontend/src/store/ui.ts
 * Task: Manages pure UI state (dialogs, active views, panels, loading spinners, breadcrumbs) globally.
 * Update: Added 'editor' to viewsWithEntitySync and ensured sidebar transitions purge explicit filter query params.
 */
import { defineStore } from 'pinia';
import router from '@/router';
import { useProjectStore } from './project';
import { useWorkspaceStore } from './workspace';

export const useUiStore = defineStore('ui', {
  state: () => {
    let initialView = 'home';
    if (typeof window !== 'undefined') {
      const path = window.location.pathname;
      if (path.includes('/dashboard')) initialView = 'dashboard';
      else if (path.includes('/editor')) initialView = 'editor';
      else if (path.includes('/maps-web')) initialView = 'mapWeb';
      else if (path.includes('/maps-sequence')) initialView = 'mapSequence';
      else if (path.includes('/maps-impactMap')) initialView = 'impactMap';
      else if (path.includes('/maps-star')) initialView = 'mapStar';
      else if (path.includes('/reports-ishikawa')) initialView = 'mapIshikawa';
      else if (path.includes('/kanban')) initialView = 'kanban';
      else if (path.includes('/matrix')) initialView = 'matrix';
      else if (path.includes('/role-profiles')) initialView = 'roledescription';
      else if (path.includes('/calendar')) initialView = 'calendar';
      else if (path.includes('/customize')) initialView = 'customize';
      else if (path.includes('/admin/projects')) initialView = 'projectAdministration';
      else if (path.includes('/admin/users')) initialView = 'userSettings';
      else if (path.includes('/admin/data')) initialView = 'dataSettings';
    }

    return {
      activeView: initialView,
      activeTabTitle: null as string | null,
      dynamicEntityTitle: null as string | null,
      panels: {
        showFilters: false,
        showSecondaryDetails: false,
        showChatbot: false
      },
      viewSettings: {
        map: { layoutType: 'force' as 'force' | 'nodePath' | 'star', depth: 2, enableDepthFilter: false },
        calendar: { selectedPeriod: 'iteration', viewDays: '5' as '5' | '7' },
        roleProfile: { currentRoleId: null as string | null }
      },
      loading: {
        datastore: false,
        save: false
      }
    };
  },

  actions: {
    setActiveView(viewName: string, resetSelection: boolean = false) {
      const projectStore = useProjectStore();
      const workspaceStore = useWorkspaceStore();
      const projectId = projectStore.loadedProject?.value;
      const currentRoute = router.currentRoute.value;

      // Reset the 3rd level breadcrumb immediately when switching root views
      this.activeTabTitle = null;
      this.dynamicEntityTitle = null;

      // Views that actively support and need the entity URL synchronization
      const viewsWithEntitySync = ['mapWeb', 'mapSequence', 'impactMap', 'mapStar', 'editor'];
      const query: any = { ...currentRoute.query };

      // Strip any prior explicit filter parameters (e.g. from Dashboard) when navigating via Sidebar
      delete query.type;

      // Handle the passing of the entity query parameter
      if (resetSelection) {
        delete query.entity;
        workspaceStore.setSelectedEntity(null);
      } else if (viewsWithEntitySync.includes(viewName) && workspaceStore.selectedEntityId) {
        query.entity = workspaceStore.selectedEntityId;
      } else if (!viewsWithEntitySync.includes(viewName)) {
        // Clear the entity param if we navigate to a view that does not need it
        delete query.entity;
      }

      // If we are already on the target route, just ensure the query is accurate and exit
      if (currentRoute.name === viewName && currentRoute.params.projectId === projectId) {
        this.activeView = viewName;
        if (viewsWithEntitySync.includes(viewName)) {
          router.replace({ query }).catch(() => {});
        }
        return;
      }

      // Route execution based on target
      if (viewName === 'home') {
        router.push({ name: 'home' });
      } else if (viewName === 'dataSettings') {
        router.push({ name: 'dataSettings' });
      } else if (viewName === 'projectAdministration') {
        router.push({ name: 'projectAdministration' });
      } else if (viewName === 'userSettings') {
        router.push({ name: 'userSettings' });
      } else if (!projectId) {
        router.push({ name: 'home' });
      } else {
        router.push({
          name: viewName,
          params: { projectId },
          query
        });
      }
    },

    setActiveTabTitle(title: string | null) {
      this.activeTabTitle = title;
    },

    updateDynamicEntityTitle() {
      const workspaceStore = useWorkspaceStore();
      if (workspaceStore.selectedEntity) {
        this.dynamicEntityTitle = workspaceStore.selectedEntity.title || workspaceStore.selectedEntity.id;
      } else {
        this.dynamicEntityTitle = null;
      }
    }
  }
});
